import { RegionShared } from "@1games-ru/region-shared";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
// import { TfaMethod } from "@/core/constants/tfaMethod";
import { type RootState } from "@/core/store";
import { Country } from "@/domains/account/constants/country";
import { IS_DEV } from "@/domains/main/constants/other";

export interface AccountState {
  selectedCharId: null | number;
  user: typeof RegionShared.Responses.GetUserResponse;
  characters: (typeof RegionShared.Responses.GetCharactersResponse)["characters"];
  activeSessions: (typeof RegionShared.Responses.GetAccountSessionsResponse)["sessions"];
  safety: (typeof RegionShared.Backend.Responses.GetAccountSettingsResponse)["account"];
  countries: [{ country: Country; short: string; code: string }];
}

const initialState: AccountState = {
  selectedCharId: null,
  user: {
    id: 0,
    socialClub: "",
    coins: 0,
    maxCharacterSlots: 0,
    charactersTotalBalance: 0,
    totalPlayingTimeWeekMinutes: 0,
    totalPlayingTimeMinutes: 0,
  },
  characters: IS_DEV
    ? [
        {
          id: 1,
          balance: 1500,
          firstName: "John",
          lastName: "Doe",
          level: 5,
          exp: 1200,
          isDeleted: false,
          faction: "Alliance",
          completeDeletedAt: null,
          lastJoinAt: new Date("2023-10-01T12:34:56Z"),
          totalPlayingTimeMinutes: 4500,
        },
      ]
    : [],

  safety: {
    email: "",
    phoneNumber: "",
    isTwoFactorGoogleEnabled: false,
    isTwoFactorEmailEnabled: false,
    isTwoFactorSmsEnabled: false,
  },
  activeSessions: [],

  countries: [{ country: Country.Russia, short: "RU", code: "+7" }],
};

const accountSlice = createSlice({
  name: "accountSlice",
  initialState,
  reducers: {
    setSelectedCharId: (state, action: PayloadAction<AccountState["selectedCharId"]>) => {
      state.selectedCharId = action.payload;
    },
    setUser: (state, action: PayloadAction<AccountState["user"]>) => {
      state.user = action.payload;
    },
    setCharacters: (state, action: PayloadAction<AccountState["characters"]>) => {
      state.characters = action.payload;
    },
    setSafety: (state, action: PayloadAction<Partial<AccountState["safety"]>>) => {
      state.safety = { ...state.safety, ...action.payload };
    },
    setActiveSessions: (state, action: PayloadAction<AccountState["activeSessions"]>) => {
      state.activeSessions = action.payload;
    },
  },
});

export const characterByIdSelector = createSelector(
  (state: RootState) => state.account.characters,
  (_, id: number) => id,
  (characters: AccountState["characters"], id: number | null) => {
    const findedCharacter = characters.find((item) => item.id === id);
    return findedCharacter;
  },
);
export const userSelector = (state: RootState) => state.account.user;
export const charactersSelector = (state: RootState) => state.account.characters;
export const selectedCharIdSelector = (state: RootState) => state.account.selectedCharId;
export const safetySelector = (state: RootState) => state.account.safety;
export const activeSessionsSelector = (state: RootState) => state.account.activeSessions;
export const countriesSelector = (state: RootState) => state.account.countries;

export const accountReducer = accountSlice.reducer;
export const { setCharacters, setUser, setSelectedCharId, setSafety, setActiveSessions } =
  accountSlice.actions;
